import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
function PaperPlane() {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-43">
      <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-44" />
    </svg>;
}
const flightPaths = [{
  // Wide sweep left to right across entire viewport
  x: ['-600px', '-200px', '0px', '200px', '400px', '600px'],
  y: ['80px', '-60px', '-120px', '-40px', '-100px', '60px'],
  rotate: [-15, -30, -5, -25, -10, -20],
  duration: 4.5,
  delay: 1.5
}, {
  // Wide sweep right to left
  x: ['600px', '300px', '100px', '-100px', '-300px', '-600px'],
  y: ['40px', '-80px', '-30px', '-100px', '-20px', '80px'],
  rotate: [195, 205, 190, 210, 195, 200],
  duration: 5,
  delay: 1
}, {
  // High arc across full width
  x: ['-600px', '-150px', '50px', '250px', '600px'],
  y: ['60px', '-140px', '-180px', '-100px', '70px'],
  rotate: [-20, -40, -10, -25, -15],
  duration: 4,
  delay: 2
}];
export function Hero() {
  const [pathIndex, setPathIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => {
    const path = flightPaths[pathIndex];
    const totalTime = (path.duration + path.delay) * 1000 + 4000; // flight + pause
    const timer = setTimeout(() => {
      setPathIndex(prev => (prev + 1) % flightPaths.length);
      setAnimKey(prev => prev + 1);
    }, totalTime);
    return () => clearTimeout(timer);
  }, [pathIndex, animKey]);
  const currentPath = flightPaths[pathIndex];
  return <section className="min-h-screen flex flex-col justify-center items-center relative px-6 pt-20 pb-10 overflow-hidden" data-id="element-45">
      {/* Flying paper plane */}
      <div className="absolute inset-0 pointer-events-none z-20" data-id="element-46">
        <motion.div key={animKey} className="absolute text-terracotta/40" style={{
        top: '50%',
        left: '50%'
      }} initial={{
        x: currentPath.x[0],
        y: currentPath.y[0],
        rotate: currentPath.rotate[0],
        opacity: 0
      }} animate={{
        x: currentPath.x,
        y: currentPath.y,
        rotate: currentPath.rotate,
        opacity: [0, 1, 1, 1, 1, 0]
      }} transition={{
        duration: currentPath.duration,
        delay: currentPath.delay,
        ease: 'easeInOut',
        times: currentPath.x.length === 6 ? [0, 0.15, 0.4, 0.6, 0.85, 1] : [0, 0.2, 0.5, 0.8, 1]
      }} data-id="element-47">
          <PaperPlane data-id="element-48" />
        </motion.div>

        {/* Trail effect — a faint dotted line that follows */}
        <motion.div key={`trail-${animKey}`} className="absolute" style={{
        top: '50%',
        left: '50%'
      }} initial={{
        x: currentPath.x[0],
        y: currentPath.y[0],
        opacity: 0
      }} animate={{
        x: currentPath.x,
        y: currentPath.y,
        opacity: [0, 0.15, 0.15, 0.15, 0.15, 0]
      }} transition={{
        duration: currentPath.duration,
        delay: currentPath.delay + 0.08,
        ease: 'easeInOut',
        times: currentPath.x.length === 6 ? [0, 0.15, 0.4, 0.6, 0.85, 1] : [0, 0.2, 0.5, 0.8, 1]
      }} data-id="element-49">
          <div className="w-2 h-2 rounded-full bg-terracotta/30" data-id="element-50" />
        </motion.div>
      </div>

      <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10" data-id="element-51">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="w-16 h-1 bg-terracotta mx-auto mb-8 rounded-full" data-id="element-52" />

        <motion.h1 className="text-5xl md:text-7xl font-light text-ink leading-[1.1]" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} data-id="element-53">
          Vi hjälper dig leda bättre. <br className="hidden md:block" data-id="element-54" />
          <span className="text-terracotta" data-id="element-55">En dag i veckan.</span>
        </motion.h1>

        <motion.div className="space-y-4" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: 'easeOut'
      }} data-id="element-56">
          <p className="text-xl md:text-2xl text-muted font-light max-w-2xl mx-auto leading-relaxed" data-id="element-57">
            Inte för att du är dålig på det. Utan för att du har för mycket
            annat att göra.
          </p>

          <p className="text-sm text-muted italic" data-id="element-58">
            Det är inte ett problem. Det är ett symptom.
          </p>
        </motion.div>

        <motion.div className="pt-8 flex flex-col items-center gap-6" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6,
        ease: 'easeOut'
      }} data-id="element-59">
          <button className="bg-terracotta hover:bg-terracotta/90 text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-200 hover:scale-105 shadow-lg shadow-terracotta/20" onClick={() => window.location.href = 'mailto:peter@humble-consulting.se'} data-id="element-60">
            Boka ett samtal
          </button>

          <a href="#services" className="text-muted hover:text-ink transition-colors text-sm flex items-center gap-2 group" data-id="element-61">
            Läs mer om hur vi jobbar
            <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" data-id="element-62" />
          </a>
        </motion.div>
      </div>
    </section>;
}